// eslint-disable-next-line import/no-unassigned-import
import "@mantine/core/styles.css";
// eslint-disable-next-line import/no-unassigned-import
import "@mantine/carousel/styles.css";
import { Box, createTheme, MantineProvider } from "@mantine/core";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import * as React from "react";
import { createRoot } from "react-dom/client";
import { HeroBanner } from "./components/hero-banner";
import { SiteHeader } from "./components/site-header";
import { ToolsSection } from "./components/tools-section";

import { Language, DEFAULT_LANGUAGE } from "./types/types";
import { SiteFooter } from "./components/site-footer";
import { LanguageContext } from "./contexts/app-context";

const LANGUAGE_STORAGE_KEY = "toolkit-language";

function getStoredLanguage(): Language {
  const storedLanguage = window.localStorage.getItem(LANGUAGE_STORAGE_KEY);

  return storedLanguage === "en_GB" || storedLanguage === "sv_SE" ? storedLanguage : DEFAULT_LANGUAGE;
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

function App(): JSX.Element {
  const [language, setLanguage] = React.useState<Language>(() => getStoredLanguage());

  const handleSetLanguage = React.useCallback((nextLanguage: Language): void => {
    window.localStorage.setItem(LANGUAGE_STORAGE_KEY, nextLanguage);
    setLanguage(nextLanguage);
  }, []);

  return (
    <LanguageContext.Provider value={language}>
      <Box mih="100vh">
        <SiteHeader />
        <HeroBanner />
        <ToolsSection />
        <SiteFooter setLanguage={handleSetLanguage} />
      </Box>
    </LanguageContext.Provider>
  );
}

const container = document.getElementById("root");

if (!container) {
  throw new Error("Bad.");
}

const theme = createTheme({
  primaryColor: "gray",
  autoContrast: true,
  fontFamily: "DINPro, sans-serif",
  headings: { fontFamily: "DINPro, sans-serif" },
  defaultRadius: "md",
  focusRing: "auto",
});

const root = createRoot(container);

root.render(
  <React.StrictMode>
    <MantineProvider theme={theme}>
      <QueryClientProvider client={queryClient}>
        <App />
        <ReactQueryDevtools initialIsOpen={false} />
      </QueryClientProvider>
    </MantineProvider>
  </React.StrictMode>
);
